import React, { useEffect, useState, useRef } from 'react';
import { db } from '../../services/firebase';
import { collection, onSnapshot, updateDoc, doc, query, orderBy, deleteDoc } from 'firebase/firestore';
import AdminNotifications from './AdminNotifications';

const STATUS_COLORS = {
  pending: '#f39c12',
  accepted: '#27ae60',
  paid: '#2980b9',
  completed: '#222',
  canceled: '#e74c3c',
};

const STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  paid: 'Paid',
  completed: 'Completed',
  canceled: 'Canceled',
};

const CALL_SOUND_URL = '/images/ding.mp3';

const CashierTab = () => {
  const [orders, setOrders] = useState([]);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const callAudioRef = useRef(null);
  const [lastCallIds, setLastCallIds] = useState({});
  const [prevItemCounts, setPrevItemCounts] = useState({});
  const [newItemOrderIds, setNewItemOrderIds] = useState([]);
  const [prevOrderStatuses, setPrevOrderStatuses] = useState({});

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const list = [];
      snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
      setOrders(list);
    });
    return () => unsub();
  }, []);

  // Play sound if there are any pending orders, with 2s pause loop
  useEffect(() => {
    let timeoutId;
    const hasPending = orders.some(order => order.status === 'pending');
    let stopped = false;
    async function playLoop() {
      if (audioRef.current && hasPending && !stopped) {
        try { await audioRef.current.play(); } catch {}
        timeoutId = setTimeout(playLoop, 2000);
      }
    }
    if (hasPending) {
      setPlaying(true);
      playLoop();
    } else {
      setPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [orders]);

  // Play sound if any new callWaiter or callBill is true and was not true before, and loop with 2s pause until handled
  useEffect(() => {
    let timeoutId;
    const newCalls = {};
    let shouldPlay = false;
    let hasActiveCall = false;
    orders.forEach(order => {
      if ((order.callWaiter || order.callBill) && !lastCallIds[order.id]) {
        shouldPlay = true;
        newCalls[order.id] = true;
      } else if ((order.callWaiter || order.callBill) && lastCallIds[order.id]) {
        newCalls[order.id] = true;
      }
      if (order.callWaiter || order.callBill) {
        hasActiveCall = true;
      }
    });
    setLastCallIds(newCalls);
    let stopped = false;
    async function playLoop() {
      if (callAudioRef.current && hasActiveCall && !stopped) {
        try { await callAudioRef.current.play(); } catch {}
        timeoutId = setTimeout(playLoop, 2000);
      }
    }
    if (shouldPlay && callAudioRef.current) {
      playLoop();
    }
    if (!hasActiveCall && callAudioRef.current) {
      callAudioRef.current.pause();
      callAudioRef.current.currentTime = 0;
    }
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [orders]);

  // Notify only when an order transitions from 'draft' to 'pending' (submitted)
  useEffect(() => {
    let shouldNotify = false;
    const newStatuses = {};
    orders.forEach(order => {
      newStatuses[order.id] = order.status;
      if (
        prevOrderStatuses[order.id] === 'draft' &&
        order.status === 'pending'
      ) {
        shouldNotify = true;
      }
    });
    setPrevOrderStatuses(newStatuses);
    if (shouldNotify && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [orders]);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'orders', id), { status });
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    await deleteDoc(doc(db, 'orders', id));
  };

  // Responsive grid columns
  const getGridStyles = () => {
    return {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 36,
      width: '100%',
      maxWidth: 900,
      margin: '0 auto',
      padding: '36px 16px 100px 16px',
      boxSizing: 'border-box',
    };
  };

  // Card animation
  const cardAnim = {
    transition: 'box-shadow 0.18s, border 0.18s, transform 0.18s',
    willChange: 'transform',
  };

  // Top bar
  const TopBar = () => (
    <div style={{
      position: 'sticky',
      top: 0,
      left: 0,
      width: '100vw',
      zIndex: 100,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(8px)',
      boxShadow: '0 2px 24px #e0e0e033',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 80,
      borderBottom: '1.5px solid #e0e0e0',
    }}>
      <span style={{ fontWeight: 900, fontSize: 38, letterSpacing: 1, color: '#222', fontFamily: 'Archivo Black, Inter, Arial, sans-serif', textShadow: '0 2px 12px #e0e0e022' }}>Cashier</span>
      {playing && <span style={{ marginLeft: 24, background: '#f39c12', color: '#fff', borderRadius: 18, padding: '8px 22px', fontWeight: 900, fontSize: 20, boxShadow: '0 2px 12px #f39c1211', animation: 'pulse 1.2s infinite alternate' }}>New Order!</span>}
      <style>{`@keyframes pulse { 0% { box-shadow: 0 0 0 0 #f39c1244; } 100% { box-shadow: 0 0 16px 8px #f39c1222; } }`}</style>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', width: '100vw', background: 'linear-gradient(135deg, #f4f6fa 70%, #e9ecf3 100%)', fontFamily: 'Inter, Archivo Black, Arial, sans-serif', overflowX: 'hidden', paddingTop: 32, paddingBottom: 32 }}>
      <audio ref={audioRef} src="/images/ding.mp3" preload="auto" />
      <audio ref={callAudioRef} src={CALL_SOUND_URL} preload="auto" />
      <TopBar />
      
      {/* Admin Notifications Section */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
        <AdminNotifications />
      </div>
      {orders.length === 0 ? (
        <div style={{ color: '#bbb', fontSize: 28, textAlign: 'center', marginTop: 120, fontWeight: 900, letterSpacing: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none"><rect x="3" y="7" width="18" height="13" rx="3" fill="#f4f6fa" stroke="#bbb" strokeWidth="2.2"/><rect x="7" y="3" width="10" height="6" rx="2" fill="#fff" stroke="#bbb" strokeWidth="2.2"/></svg>
          No orders yet.
        </div>
      ) : (
        <div style={getGridStyles()}>
          {orders.filter(order => !order.draft).map(order => (
            <div key={order.id} style={{
              background: 'linear-gradient(120deg, #fff 80%, #f7f8fa 100%)',
              border: '1.5px solid #e0e0e0',
              borderRadius: 18,
              boxShadow: `${newItemOrderIds.includes(order.id) ? '0 0 0 4px #27ae60aa, ' : ''}0 12px 48px #2222, 0 1.5px 0 #fff inset`,
              padding: '44px 36px 32px 36px',
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              minHeight: 340,
              position: 'relative',
              overflow: 'hidden',
              ...cardAnim,
              width: '100%',
              boxSizing: 'border-box',
            }}>
              {/* Header row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 900, fontSize: 34, letterSpacing: 0.5, color: '#27ae60', textShadow: '0 2px 12px #e0e0e022' }}>Table {order.tableNumber || 'N/A'}</div>
                <div style={{ fontWeight: 900, fontSize: 20, color: '#fff', background: STATUS_COLORS[order.status] || '#bbb', borderRadius: 22, padding: '8px 28px', marginLeft: 12, textTransform: 'capitalize', boxShadow: '0 2px 12px #e0e0e022', letterSpacing: 1, transition: 'background 0.18s', minWidth: 120, textAlign: 'center' }}>{STATUS_LABELS[order.status] || order.status}</div>
                <div style={{ color: '#888', fontSize: 16, fontWeight: 500, background: '#f7f7f7', borderRadius: 12, padding: '6px 18px', marginLeft: 12 }}>{order.createdAt?.toDate ? order.createdAt.toDate().toLocaleString() : ''}</div>
              </div>
              {/* Order short ID (visual match to order-status) */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ color: '#222', fontSize: 15, fontWeight: 700, background: '#f4f6fa', borderRadius: 10, padding: '3px 10px', letterSpacing: 1, userSelect: 'all', cursor: 'pointer', boxShadow: '0 1px 4px #e0e0e011', opacity: 0.7 }} title="Copy Order ID" onClick={() => {navigator.clipboard.writeText(order.id)}}>
                  Order #{order.id.slice(-5).toUpperCase()}
                </div>
              </div>
              {/* Full Order ID for copy/reference */}
              <div style={{ color: '#aaa', fontSize: 12, fontWeight: 500, marginBottom: 6, wordBreak: 'break-all', userSelect: 'all', textAlign: 'right' }}>
                {order.id}
              </div>
              {/* Notes */}
              {order.notes && <div style={{ background: '#f9f6e7', color: '#b48a00', borderRadius: 10, padding: '10px 18px', fontWeight: 900, marginBottom: 8, fontSize: 16, letterSpacing: 0.5 }}>Note: {order.notes}</div>}
              {/* Items table */}
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, background: 'rgba(255,255,255,0.92)', borderRadius: 12, boxShadow: '0 1px 4px #e0e0e011' }}>
                  <thead>
                    <tr style={{ background: '#f7f7f7', color: '#888', fontWeight: 900 }}>
                      <th style={{ textAlign: 'left', padding: 8, minWidth: 120 }}>Item</th>
                      <th style={{ textAlign: 'left', padding: 8, minWidth: 90 }}>Bean</th>
                      <th style={{ textAlign: 'left', padding: 8, minWidth: 60 }}>Temp</th>
                      <th style={{ textAlign: 'center', padding: 8, minWidth: 40 }}>Qty</th>
                      <th style={{ textAlign: 'left', padding: 8, minWidth: 100 }}>Note</th>
                      <th style={{ textAlign: 'right', padding: 8, minWidth: 70 }}>Price</th>
                      <th style={{ textAlign: 'left', padding: 8, minWidth: 80 }}>Sweetness</th>
                      <th style={{ textAlign: 'left', padding: 8, minWidth: 80 }}>Milk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, idx) => (
                      <tr key={idx} style={{
                        borderBottom: '1px solid #eee',
                        transition: 'background 0.18s',
                        background: item.seenByCashier === false ? '#e8ffe8' : undefined,
                        fontWeight: item.seenByCashier === false ? 900 : undefined,
                        position: 'relative',
                      }}>
                        <td style={{ fontWeight: 900, padding: 8, position: 'relative' }}>
                          {item.name}
                          {item.seenByCashier === false && (
                            <span style={{
                              display: 'inline-block',
                              background: '#27ae60',
                              color: '#fff',
                              borderRadius: 8,
                              padding: '2px 10px',
                              fontWeight: 900,
                              fontSize: 13,
                              marginLeft: 10,
                              boxShadow: '0 2px 8px #27ae6011',
                              letterSpacing: 1,
                              animation: 'pulseNew 1.2s infinite alternate',
                              verticalAlign: 'middle',
                            }}>
                              NEW
                            </span>
                          )}
                        </td>
                        <td style={{ color: '#888', fontWeight: 700, padding: 8 }}>
                          {item.bean ? `${item.bean.name}${item.bean.origin ? ' (' + item.bean.origin + ')' : ''}` : '-'}
                        </td>
                        <td style={{ color: '#888', fontWeight: 700, padding: 8 }}>
                          {(() => {
                            const n = (item.name || '').toLowerCase();
                            if (n.includes('iced passion fruit tea') || n.includes('iced hibiscus tea') || n.includes('iced hibuscus tea')) {
                              return '-';
                            }
                            return item.espresso
                              ? (item.espresso.charAt(0).toUpperCase() + item.espresso.slice(1))
                              : (item.temp ? item.temp.charAt(0).toUpperCase() + item.temp.slice(1) : '-');
                          })()}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 900, padding: 8 }}>{item.quantity}</td>
                        <td style={{ color: '#888', padding: 8 }}>{item.itemNote || '-'}</td>
                        <td style={{ textAlign: 'right', color: '#222', fontWeight: 900, padding: 8 }}>QAR {item.price}</td>
                        <td style={{ color: '#888', fontWeight: 700, padding: 8 }}>{item.name && item.name.toLowerCase().includes('matcha latte') ? (item.sweetness + '%') : '-'}</td>
                        <td style={{ color: '#888', fontWeight: 700, padding: 8 }}>{item.name && item.name.toLowerCase().includes('matcha latte') ? (item.milkType || '-') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Subtotal */}
              <div style={{ fontWeight: 900, fontSize: 22, color: '#222', margin: '18px 0 0 0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                <span style={{ marginRight: 18 }}>Total:</span>
                <span style={{ background: 'rgba(247,248,250,0.95)', color: '#222', borderRadius: 16, padding: '8px 28px', fontWeight: 900, fontSize: 24, border: '1.5px solid #e0e0e0', boxShadow: '0 1px 4px #e0e0e011' }}>
                  QAR {order.items?.reduce((sum, i) => sum + (i.price * i.quantity), 0).toFixed(2)}
                </span>
              </div>
              {/* Call Waiter / Bill Badge */}
              {(order.callWaiter || order.callBill) && (
                <div style={{
                  position: 'absolute',
                  top: 18,
                  right: 24,
                  background: order.callWaiter ? '#27ae60' : '#f39c12',
                  color: '#fff',
                  borderRadius: 16,
                  padding: '6px 18px',
                  fontWeight: 900,
                  fontSize: 16,
                  boxShadow: '0 2px 12px #e0e0e022',
                  zIndex: 10,
                  letterSpacing: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  {order.callWaiter && <span>🛎️ Waiter</span>}
                  {order.callBill && <span>💵 Bill</span>}
                </div>
              )}
              {/* Status buttons */}
              <div style={{
                marginTop: 24,
                display: 'flex',
                gap: 18,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                position: 'sticky',
                bottom: 0,
                background: 'rgba(255,255,255,0.82)',
                borderRadius: 12,
                boxShadow: '0 2px 12px #e0e0e022',
                padding: '14px 0 0 0',
                zIndex: 2,
              }}>
                {order.status === 'pending' && (
                  <button
                    onClick={async () => {
                      // Mark all items as seen and accept the order
                      const updatedItems = order.items?.map(i => ({ ...i, seenByCashier: true })) || [];
                      await updateDoc(doc(db, 'orders', order.id), { items: updatedItems, status: 'accepted' });
                    }}
                    style={{
                      background: '#27ae60',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      padding: '12px 32px',
                      fontWeight: 900,
                      fontSize: 20,
                      cursor: 'pointer',
                      transition: 'background 0.18s',
                      boxShadow: '0 2px 12px #27ae6011'
                    }}
                  >
                    Accept Order
                  </button>
                )}
                {/* Show PAID BY CARD label if paid by card, otherwise show Mark as Paid button if accepted */}
                {order.status === 'paid' && order.payMethod === 'card' ? (
                  <div style={{
                    background: '#2980b9',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '12px 32px',
                    fontWeight: 900,
                    fontSize: 20,
                    display: 'inline-block',
                    letterSpacing: 1,
                    marginRight: 8,
                  }}>
                    PAID BY CARD
                  </div>
                ) : order.status === 'accepted' && (
                  <button onClick={() => updateStatus(order.id, 'paid')} style={{ background: '#2980b9', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 32px', fontWeight: 900, fontSize: 20, cursor: 'pointer', transition: 'background 0.18s', boxShadow: '0 2px 12px #2980b911' }}>Mark as Paid</button>
                )}
                {order.status === 'paid' && (
                  <button disabled style={{ background: '#d4efdf', color: '#222', border: 'none', borderRadius: 12, padding: '12px 32px', fontWeight: 900, fontSize: 20, opacity: 0.7 }}>Completed</button>
                )}
                <button onClick={() => updateStatus(order.id, 'completed')} disabled={order.status === 'completed'} style={{ background: order.status === 'completed' ? '#d6eaf8' : '#222', color: order.status === 'completed' ? '#222' : '#fff', border: 'none', borderRadius: 12, padding: '12px 32px', fontWeight: 900, fontSize: 20, cursor: order.status === 'completed' ? 'not-allowed' : 'pointer', opacity: order.status === 'completed' ? 0.7 : 1, transition: 'background 0.18s', boxShadow: '0 2px 12px #2222' }}>Mark as Completed</button>
                <button onClick={() => updateStatus(order.id, 'canceled')} disabled={order.status === 'canceled'} style={{ background: order.status === 'canceled' ? '#fadbd8' : '#e74c3c', color: order.status === 'canceled' ? '#222' : '#fff', border: 'none', borderRadius: 12, padding: '12px 32px', fontWeight: 900, fontSize: 20, cursor: order.status === 'canceled' ? 'not-allowed' : 'pointer', opacity: order.status === 'canceled' ? 0.7 : 1, transition: 'background 0.18s', boxShadow: '0 2px 12px #e74c3c11' }}>Cancel</button>
                <button onClick={() => deleteOrder(order.id)} style={{ background: '#fff', color: '#e74c3c', border: '2px solid #e74c3c', borderRadius: 12, padding: '12px 32px', fontWeight: 900, fontSize: 20, cursor: 'pointer', transition: 'background 0.18s', boxShadow: '0 2px 12px #e74c3c11' }}>Delete Order</button>
                {(order.callWaiter || order.callBill) && (
                  <button onClick={async () => {
                    await updateDoc(doc(db, 'orders', order.id), { callWaiter: false, callBill: false });
                  }} style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 28px', fontWeight: 900, fontSize: 18, cursor: 'pointer', transition: 'background 0.18s', boxShadow: '0 2px 12px #27ae6011' }}>
                    Mark Call as Handled
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes pulseNew { 0% { box-shadow: 0 0 0 0 #27ae6044; } 100% { box-shadow: 0 0 12px 6px #27ae6022; } }`}</style>
    </div>
  );
};

export default CashierTab; 